import { format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { v4 as uuidv4 } from 'uuid';
import { DAY_DATE_FORMAT, SHORT_HOUR_DATE_FORMAT, STORAGE } from '@/helpers/constants';
import { changeTimeZoneFormat, convertLocaleToUTC, deepClone } from '@/helpers/utils';
// eslint-disable-next-line import/no-cycle
import { validateVolume } from '@/helpers/campaignValidations';
// eslint-disable-next-line import/no-cycle
import { getOriginValuesNewState, mapAggregateModelSelections } from '../volumeDetails-utils';

const addDates = (newOptions, newState, headerLanguage) => {
  const arrayDates = newOptions.map((option) => (
    option === 'Date'
      ? {
        calendarDate: '',
        textDate: {
          uuid: uuidv4(),
          translations: {
            [headerLanguage]: '',
          },
        },
        timeDate: '',
      }
      : option));
  newState.forEach((element, index) => {
    arrayDates[index].calendarDate = element.calendarDate;
    arrayDates[index].textDate.translations[headerLanguage] = element.textDate.translations[headerLanguage] || '';
    arrayDates[index].timeDate = element.timeDate || '';
  });

  return arrayDates;
};

const addOtherDescriptions = (newOptions, newState, headerLanguage) => {
  const arrayInputs = newOptions.map((option) => (
    option === 'Other description'
      ? {
        uuid: uuidv4(),
        translations: {
          [headerLanguage]: '',
        },
      }
      : option));
  newState.forEach((element, index) => {
    arrayInputs[index] = element;
  });

  return arrayInputs;
};

const addReleaseState = (newOptions, newState, section, headerLanguage) => {
  const arrayReleases = newOptions.map((option) => (
    option === 'Releases'
      ? {
        releaseName: '',
        additionalInfo: {
          uuid: uuidv4(),
          translations: {
            [headerLanguage]: '',
          },
        },
        ...(section === 'VehicleCleanDirt' ? { moreUpcomingReleases: '' } : {}),
      }
      : option));
  newState.forEach((element, index) => {
    arrayReleases[index].releaseName = element.releaseName;
    arrayReleases[index].additionalInfo.translations[headerLanguage] = element.additionalInfo.translations[headerLanguage] || '';

    if (section === 'VehicleCleanDirt') arrayReleases[index].moreUpcomingReleases = element.moreUpcomingReleases || '';
  });

  return arrayReleases;
};

const addOthersInputs = (newOptions, newState) => {
  const arrayInputs = newOptions.map(() => '');
  newState.forEach((element, index) => {
    arrayInputs[index] = element;
  });

  return arrayInputs;
};

const getNewVolumeState = (attribute, newOptions, newState, section, headerLanguage) => {
  switch (attribute) {
    case 'dates':
      return addDates(newOptions, newState, headerLanguage);
    case 'otherDescriptions':
      return addOtherDescriptions(newOptions, newState, headerLanguage);
    case 'fromStartOfProductions':
      return newOptions.map(() => 'From Start of production');
    case 'releases':
      return addReleaseState(newOptions, newState, section, headerLanguage);
    default:
      return addOthersInputs(newOptions, newState);
  }
};

export const validateVehicleVolume = (volumes, headerLanguage, validations, setPlantsValidations, plantSelected, limitationPlants) => {
  if (validations?.active?.all && plantSelected) {
    const volumeSelected = volumes?.find((currentVolume) => currentVolume?.plant === plantSelected?.wkzNumber);
    const limitationPlant = limitationPlants?.[volumeSelected?.plant];
    const newState = {
      ...validations?.affectedVehicles,
      [headerLanguage]: validateVolume(validations?.affectedVehicles?.[headerLanguage], volumeSelected, limitationPlant, headerLanguage),
    };

    setPlantsValidations(newState);
  }
};

export const validateAddedVolumes = (volumes, headerLanguage, validations, setPlantsValidations, plants, limitationPlants) => {
  if (validations?.active?.all) {
    const wkzs = plants.map(({ wkzNumber }) => wkzNumber);
    const newState = volumes.filter(({ plant }) => wkzs.includes(plant))
      .reduce((acc, volume) => {
        const limitationPlant = limitationPlants?.[volume?.plant];
        const volumeValidation = validateVolume(acc, volume, limitationPlant, headerLanguage);

        return {
          ...acc,
          [headerLanguage]: {
            ...acc?.[headerLanguage],
            volumes: {
              ...acc?.[headerLanguage]?.volumes,
              ...volumeValidation.volumes,
            },
          },
        };
      }, { ...validations?.affectedVehicles });
    setPlantsValidations(newState);
  }
};

export const mapVolumesNOKOK = (section, plantSelected, volumes, newState) => volumes?.map((currentVolume) => (currentVolume?.plant === plantSelected?.wkzNumber
  ? {
    ...currentVolume,
    limitationVolumeManualEntry: {
      ...currentVolume.limitationVolumeManualEntry,
      [section]: newState,
    },
  }
  : currentVolume));

const modifyVehicleGroup = (index, groups, newState) => {
  const newGroups = deepClone(groups);
  newGroups[index] = {
    ...newGroups[index],
    ...newState,
  };

  return newGroups;
};

export const mapVolumesNOKOKGroup = (groupID, wkzNumber, volumes, newState) => (
  volumes?.map((currentVolume) => (currentVolume?.plant === wkzNumber
    ? {
      ...currentVolume,
      limitationVolumeManualEntry: {
        ...currentVolume.limitationVolumeManualEntry,
        vehicleGroups: modifyVehicleGroup(groupID, currentVolume.limitationVolumeManualEntry.vehicleGroups, newState),
      },
    }
    : currentVolume)));

const updateVolumes = (groupId, plantSelected, volumes, newState, setVolumesOriginHadChanged, originalValues, headerLanguage,
  validations, setPlantsValidations, limitationPlants) => {
  const newVolumes = mapVolumesNOKOKGroup(groupId, plantSelected?.wkzNumber, volumes, newState);
  setVolumesOriginHadChanged({
    originalValues: getOriginValuesNewState(originalValues, plantSelected),
    volumes: newVolumes,
  });
  validateVehicleVolume(newVolumes, headerLanguage, validations, setPlantsValidations, plantSelected, limitationPlants);
};

export const selectMenuChips = (option, section, groupId, groupSection, vehiclesNOKOK, headerLanguage, volumes, plantSelected,
  setVolumesOriginHadChanged, originalValues, closeMenu, validations, setPlantsValidations, limitationPlants) => {
  const attribute = vehiclesNOKOK.find(({ value }) => value === option)?.key;

  const newState = {
    ...groupSection,
  };
  const newOptions = [...groupSection?.[attribute], option];
  newState[attribute] = getNewVolumeState(attribute, newOptions, newState[attribute], section, headerLanguage);

  updateVolumes(groupId, plantSelected, volumes, { [section]: newState }, setVolumesOriginHadChanged, originalValues,
    headerLanguage, validations, setPlantsValidations, limitationPlants);
  closeMenu('CLOSE', null, section, groupId);
};

export const getInputValue = (attribute, index, isCalendar, groupSection, stringToDate, headerLanguage, isTime, isDropdown,
  isAdditionalInput) => {
  if (isTime) {
    return groupSection?.dates[index]?.timeDate;
  }

  if (attribute === 'dates' && !isCalendar) {
    return groupSection?.dates?.[index]?.textDate.translations[headerLanguage];
  }

  if (attribute === 'dates' && isCalendar) {
    return stringToDate(groupSection?.dates[index]?.calendarDate);
  }

  if (attribute === 'ttzs') {
    return stringToDate(groupSection?.ttzs[index]);
  }

  if (attribute === 'otherDescriptions') {
    return groupSection?.otherDescriptions[index].translations[headerLanguage];
  }

  if (attribute === 'releases' && !isDropdown && !isAdditionalInput) {
    return groupSection?.releases?.[index]?.releaseName;
  }

  if (attribute === 'releases' && isDropdown) {
    return groupSection?.releases?.[index]?.moreUpcomingReleases;
  }

  if (attribute === 'releases' && isAdditionalInput) {
    return groupSection?.releases?.[index]?.additionalInfo.translations[headerLanguage];
  }

  return groupSection?.[attribute][index];
};

export const deleteItem = (groupId, attribute, index, optionsLabels, section, volumes, plantSelected, setVolumesOriginHadChanged,
  originalValues, headerLanguage, validations, setPlantsValidations, limitationPlants) => {
  const itemDelete = [...optionsLabels[attribute]];
  itemDelete.splice(index, 1);
  const newState = {
    ...optionsLabels,
    [attribute]: itemDelete,
  };

  updateVolumes(groupId, plantSelected, volumes, { [section]: newState }, setVolumesOriginHadChanged, originalValues,
    headerLanguage, validations, setPlantsValidations, limitationPlants);
};

export const handleChangeInput = (groupId, attribute, optionsLabels, index, item, section, volumes, plantSelected,
  setVolumesOriginHadChanged, originalValues, headerLanguage, isTime, isDropdown, isAdditionalInput, validations,
  setPlantsValidations, limitationPlants) => {
  const update = [...optionsLabels[attribute]];

  if (attribute === 'dates' && !isTime) {
    update[index].textDate.translations[headerLanguage] = item;
  } else if (attribute === 'dates' && isTime) {
    update[index].timeDate = item;
  } else if (attribute === 'otherDescriptions') {
    update[index].translations[headerLanguage] = item;
  } else if (attribute === 'releases' && isDropdown) {
    update[index].moreUpcomingReleases = item;
  } else if (attribute === 'releases' && !isDropdown && !isAdditionalInput) {
    update[index].releaseName = item;
  } else if (attribute === 'releases' && isAdditionalInput) {
    update[index].additionalInfo.translations[headerLanguage] = item;
  } else {
    update[index] = item;
  }

  const newState = {
    ...optionsLabels,
    [attribute]: update,
  };
  updateVolumes(groupId, plantSelected, volumes, { [section]: newState }, setVolumesOriginHadChanged, originalValues,
    headerLanguage, validations, setPlantsValidations, limitationPlants);
};

export const handleChangeDate = (groupId, attribute, optionsLabels, index, item, section, volumes, plantSelected,
  setVolumesOriginHadChanged, originalValues, headerLanguage, validations, setPlantsValidations, limitationPlants) => {
  const update = [
    ...optionsLabels[attribute],
  ];

  if (attribute === 'dates') {
    update[index].calendarDate = item;

    if (!update[index].timeDate) {
      update[index].timeDate = '00:00';
    }
  } else {
    update[index] = item;
  }
  const newState = {
    ...optionsLabels,
    [attribute]: update,
  };

  updateVolumes(groupId, plantSelected, volumes, { [section]: newState }, setVolumesOriginHadChanged, originalValues,
    headerLanguage, validations, setPlantsValidations, limitationPlants);
};

export const styleValidOnNok = (section, currTab, affectedVehiclesValidations, headerLanguage, plantSelected, validations) => {
  if (currTab === 'CLEAN_DIRTY_POINT' && validations?.active?.all) {
    const sectionInvalid = validations?.affectedVehicles?.[headerLanguage]?.volumes?.[plantSelected?.wkzNumber]?.vehiclesNOKOK?.[section]?.invalid;

    if ((section === 'vehiclesOK' || section === 'VehicleCleanDirt') && sectionInvalid && validations?.active?.all) {
      return 'error';
    }
  }
};

const mapDatesVehicleOKNOK = (section, timezone) => ({
  ...section,
  dates: section?.dates.map((date) => {
    if (!date.calendarDate || !date.timeDate) {
      return date;
    }

    const convertedDate = fromZonedTime(`${date.calendarDate} ${date.timeDate}`, timezone);

    return {
      textDate: date.textDate,
      calendarDate: format(convertLocaleToUTC(convertedDate), DAY_DATE_FORMAT),
      timeDate: format(convertLocaleToUTC(convertedDate), SHORT_HOUR_DATE_FORMAT),
    };
  }),
});

export const mapDatesVehicleGroups = (groups = [], timezone) => (
  groups.map((group) => ({
    ...group,
    cleanPoint: mapDatesVehicleOKNOK(group.cleanPoint, timezone),
    dirtPoint: mapDatesVehicleOKNOK(group.dirtPoint, timezone),
  }))
);

export const VOLUME_FIELDS = {
  selected: ['aggregateSelecteds', 'specialEquipmentSelecteds', 'countryVariantsSelecteds', 'paintCodeSelecteds',
    'vehicleModelDesignationSelecteds', 'codeRules'],
  comments: ['aggregateComment', 'countryVariantsComment', 'paintCodeComment', 'specialEquipmentComment',
    'vehicleModelDesignationComment'],
  nOKOK: ['dates', 'otherDescriptions', 'productionNumbers', 'shiftNumbers', 'ttzs', 'vehicleIdentificationNumbers',
    'fromStartOfProductions', 'releases'],
};

export const updateVolumesChangingTimeZone = (affectedVehicles, setAffectedVehicles, timeZone) => {
  const lastUTC = changeTimeZoneFormat(localStorage.getItem(STORAGE.LAST_UTC));

  const volumesUTC = affectedVehicles.volumes.map((volume) => ({
    ...volume,
    limitationVolumeManualEntry: {
      ...volume.limitationVolumeManualEntry,
      vehicleGroups: mapDatesVehicleGroups(volume.limitationVolumeManualEntry.vehicleGroups, lastUTC),
    },
  }));

  const newState = {
    ...affectedVehicles,
    volumes: mapAggregateModelSelections(volumesUTC, timeZone, true),
  };

  setAffectedVehicles(newState);
};

export const vehicleGroupEmpty = {
  selections: [],
  dirtPoint: {
    dates: [],
    fromStartOfProductions: [],
    otherDescriptions: [],
    productionNumbers: [],
    shiftNumbers: [],
    ttzs: [],
    vehicleIdentificationNumbers: [],
    releases: [],
  },
  cleanPoint: {
    dates: [],
    otherDescriptions: [],
    productionNumbers: [],
    shiftNumbers: [],
    ttzs: [],
    vehicleIdentificationNumbers: [],
    releases: [],
  },
};

export const getLimitationByPlant = (limitationPlants, plantSelected) => limitationPlants?.[plantSelected?.wkzNumber] || {};

export const getVolumeByPlant = (volumes, plantSelected) => volumes.find((v) => v.plant === plantSelected?.wkzNumber);

export const getAllModelSeries = (limitationPlant) => {
  if (Array.isArray(limitationPlant?.affectedSeries)) {
    const allModelSeriesReducer = limitationPlant?.affectedSeries?.reduce((acc, { modelSerie, typeVersions }) => acc.concat(typeVersions.map((tv) => `${modelSerie}-${tv}`)), []);

    return allModelSeriesReducer;
  }
  let allMS = Object.entries(limitationPlant?.affectedSeries || {}).map(([mSeries, typeVersions]) => ({ mSeries, typeVersions }));
  allMS = allMS.reduce((acc, { mSeries, typeVersions }) => acc.concat(typeVersions.map((tv) => `${mSeries}-${tv}`)), []);

  return allMS;
};

export const getSelectedModelSeriesByGroupTranslations = ({ selections }) => selections.map((s) => `${s.modelSeries}-${s.typeVersionCode}`);

export const getSelectedModelSeriesByGroup = ({ selections }) => selections.map((s) => `${s.modelSeries}-${s.typeVersion}`);

export const getSelectedModelSeries = (volume) => {
  let selectedMS = volume?.limitationVolumeManualEntry?.vehicleGroups;
  selectedMS = selectedMS?.reduce((acc, group) => acc.concat(getSelectedModelSeriesByGroup(group)), []) ?? [];

  return selectedMS;
};

export const modelSeriesStringToObject = (mss) => mss.map((ms) => {
  const [modelSeries, typeVersion] = ms.split('-');

  return { modelSeries, typeVersion };
});

export const getMSNotSelected = (volumes, limitationPlants, plantSelected) => {
  const allMS = getAllModelSeries(getLimitationByPlant(limitationPlants, plantSelected));
  const selectedMS = getSelectedModelSeries(getVolumeByPlant(volumes, plantSelected));

  return allMS.filter((ms) => !selectedMS.includes(ms));
};

export const putSelectionsVehicleGroupVolume = (volumes, plantSelected, groupId, selections) => {
  const newVolumes = volumes.map((vol) => (vol.plant === plantSelected.wkzNumber
    ? {
      ...vol,
      limitationVolumeManualEntry: {
        ...vol.limitationVolumeManualEntry,
        vehicleGroups: vol.limitationVolumeManualEntry.vehicleGroups?.map((vg, index) => (index === groupId
          ? { ...vg, selections }
          : vg)),
      },
    }
    : vol));

  return newVolumes;
};

export const removeEmptyGroups = (newVolumes, wkzNumber) => {
  const volumesNew = deepClone(newVolumes);
  const volume = volumesNew.find((vol) => vol.plant === wkzNumber);

  if (volume) {
    volume.limitationVolumeManualEntry.vehicleGroups = volume.limitationVolumeManualEntry.vehicleGroups
      .filter(({ selections }) => selections.length > 0);
  }

  return volumesNew;
};

export const filterGroupsByAllowedMS = (volumes, limitationPlants) => {
  let volumesNew = deepClone(volumes);
  let hasChange = false;

  volumesNew = volumesNew.map((volume) => {
    const wkzNumberPlant = volume.plant;

    if (!limitationPlants[wkzNumberPlant]) return volume;

    const allowedMS = getAllModelSeries(limitationPlants[wkzNumberPlant]);

    const groups = volume.limitationVolumeManualEntry.vehicleGroups.map((group) => {
      const modelSeriesSelected = getSelectedModelSeriesByGroup(group);
      const modelSeriesSelectedFiltered = modelSeriesSelected.filter((ms) => allowedMS.includes(ms));
      hasChange = hasChange || (limitationPlants[wkzNumberPlant] && modelSeriesSelected.length !== modelSeriesSelectedFiltered.length);

      return {
        ...group,
        selections: modelSeriesStringToObject(modelSeriesSelectedFiltered),
      };
    });

    return {
      ...volume,
      limitationVolumeManualEntry: {
        ...volume.limitationVolumeManualEntry,
        vehicleGroups: groups,
      },
    };
  });

  return [volumesNew, hasChange];
};
